import React, { useEffect, useMemo, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { v4 as uuidv4 } from "uuid"

import {
  BranchCase,
  BranchNodeData,
  CHECK_KIND_GROUP,
  CHECK_KIND_LEAF,
  CheckExpression,
  CheckGroup,
  CheckLeaf,
  OPERATOR_AND,
  WorkflowNode
} from "lib/types/workflow"
import Input from "components/Ui/Form/Input"

import { MAX_PARALLEL_CHILDREN, NODE_TYPE_BRANCH } from "../nodeKind"

import LeafCheckEditor from "./LeafCheckEditor"
import CheckGroupEditor from "./CheckGroupEditor"
import { getExpressionProblems, isLeafValid } from "./conditionFormHelpers"
import { makeDefaultLeaf } from "./checkFieldHelpers"
import {
  BranchCaseActions,
  BranchCaseBody,
  BranchCaseCard,
  BranchCaseHeader,
  BranchCaseTitle,
  BranchLimitHint,
  BranchOtherwiseCard,
  FormSection,
  SaveButton,
  SmallActionButton,
  ValidationProblems,
  ValidationProblemsList
} from "./NodeEditorSidebar.styled"

type Props = {
  node: WorkflowNode
  onSave: (nodeId: string, data: BranchNodeData) => void
  hideSave?: boolean
  /** Focus the name input of the case with this id once after mount. Set by
   *  the canvas when the inline "+" button appends a fresh case. */
  autoFocusCaseId?: string
}

interface FormValues {
  label: string
}

const isExpressionValid = (expr: CheckExpression): boolean => {
  if (expr.kind === CHECK_KIND_LEAF) return isLeafValid(expr)
  if (expr.children.length < 2) return false
  return expr.children.every(isExpressionValid)
}

const newCase = (label: string): BranchCase => ({
  id: uuidv4(),
  label,
  expression: makeDefaultLeaf()
})

const BranchNodeForm = ({
  node,
  onSave,
  hideSave = false,
  autoFocusCaseId
}: Props) => {
  const data = node.data as BranchNodeData

  const [cases, setCases] = useState<BranchCase[]>(() => data.cases)
  const [caseLabels, setCaseLabels] = useState<string[]>(() =>
    data.cases.map((c) => c.label)
  )

  // When the inline "+" on the canvas appends a case and opens this editor,
  // focus the new case's name input + scroll it into view so the user lands
  // exactly where they need to type.
  const focusedRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!autoFocusCaseId || focusedRef.current === autoFocusCaseId) return
    const el = document.getElementById(`input-case-${autoFocusCaseId}-label`)
    if (el instanceof HTMLInputElement) {
      el.focus()
      el.scrollIntoView({ behavior: "smooth", block: "center" })
      focusedRef.current = autoFocusCaseId
    }
  }, [autoFocusCaseId, cases])

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormValues>({ defaultValues: { label: data.label } })

  const updateExpression = (index: number, next: CheckExpression) => {
    setCases((cs) => cs.map((c, i) => (i === index ? { ...c, expression: next } : c)))
  }

  const promoteToGroup = (index: number) => {
    setCases((cs) =>
      cs.map((c, i) => {
        if (i !== index || c.expression.kind !== CHECK_KIND_LEAF) return c
        const group: CheckGroup = {
          kind: CHECK_KIND_GROUP,
          operator: OPERATOR_AND,
          children: [c.expression, makeDefaultLeaf()]
        }
        return { ...c, expression: group }
      })
    )
  }

  const handleAddCase = () => {
    if (cases.length >= MAX_PARALLEL_CHILDREN) return
    const label = `Case ${cases.length + 1}`
    setCases((cs) => [...cs, newCase(label)])
    setCaseLabels((labels) => [...labels, label])
  }

  const handleRemoveCase = (index: number) => {
    if (cases.length <= 2) return
    setCases((cs) => cs.filter((_, i) => i !== index))
    setCaseLabels((labels) => labels.filter((_, i) => i !== index))
  }

  const handleMoveCase = (index: number, direction: -1 | 1) => {
    const next = index + direction
    if (next < 0 || next >= cases.length) return
    setCases((cs) => {
      const copy = [...cs]
      ;[copy[index], copy[next]] = [copy[next], copy[index]]
      return copy
    })
    setCaseLabels((labels) => {
      const copy = [...labels]
      ;[copy[index], copy[next]] = [copy[next], copy[index]]
      return copy
    })
  }

  const handleLabelChange = (index: number, value: string) => {
    setCaseLabels((labels) => labels.map((l, i) => (i === index ? value : l)))
  }

  const isValid = useMemo(
    () =>
      cases.length >= 2 &&
      cases.every((c) => isExpressionValid(c.expression)) &&
      caseLabels.every((l) => l.trim().length > 0),
    [cases, caseLabels]
  )

  const problems = useMemo(() => {
    if (isValid) return []
    const out: string[] = []
    if (cases.length < 2) out.push("A branch needs at least 2 cases.")
    cases.forEach((c, i) => {
      if (!caseLabels[i]?.trim()) out.push(`Case ${i + 1} needs a name.`)
      const inner = getExpressionProblems(c.expression)
      for (const p of inner) out.push(`Case ${i + 1}: ${p}`)
    })
    return out
  }, [cases, caseLabels, isValid])

  const [showProblems, setShowProblems] = useState(false)

  const onSubmit = (values: FormValues) => {
    if (!isValid) {
      setShowProblems(true)
      return
    }
    onSave(node.id, {
      nodeType: NODE_TYPE_BRANCH,
      label: values.label,
      cases: cases.map((c, i) => ({ ...c, label: caseLabels[i] }))
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormSection>
        <Input
          name="label"
          label="Name"
          register={register}
          validation={{
            required: "Name is required",
            maxLength: { value: 100, message: "Name must be at most 100 characters" }
          }}
          errors={errors}
        />
      </FormSection>

      {cases.map((c, i) => (
        <BranchCaseCard key={c.id}>
          <BranchCaseHeader>
            <BranchCaseTitle>
              Case {i + 1}
              {caseLabels[i]?.trim() ? ` · ${caseLabels[i]}` : ""}
            </BranchCaseTitle>
            <BranchCaseActions>
              <SmallActionButton
                type="button"
                onClick={() => handleMoveCase(i, -1)}
                disabled={i === 0}
                aria-label="Move case up"
              >
                ↑
              </SmallActionButton>
              <SmallActionButton
                type="button"
                onClick={() => handleMoveCase(i, 1)}
                disabled={i === cases.length - 1}
                aria-label="Move case down"
              >
                ↓
              </SmallActionButton>
              <SmallActionButton
                type="button"
                $destructive
                onClick={() => handleRemoveCase(i)}
                disabled={cases.length <= 2}
              >
                Remove
              </SmallActionButton>
            </BranchCaseActions>
          </BranchCaseHeader>

          <BranchCaseBody>
            <Input
              name={`case-${c.id}-label`}
              label="Case name"
              value={caseLabels[i] ?? ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleLabelChange(i, e.target.value)
              }
            />

            {c.expression.kind === CHECK_KIND_LEAF ? (
              <>
                <LeafCheckEditor
                  value={c.expression}
                  onChange={(next: CheckLeaf) => updateExpression(i, next)}
                />
                <FormSection>
                  <SmallActionButton type="button" onClick={() => promoteToGroup(i)}>
                    + Add check (combine with AND/OR)
                  </SmallActionButton>
                </FormSection>
              </>
            ) : (
              <CheckGroupEditor
                value={c.expression}
                onChange={(next: CheckGroup) => updateExpression(i, next)}
                onUnwrap={(child: CheckExpression) => updateExpression(i, child)}
              />
            )}
          </BranchCaseBody>
        </BranchCaseCard>
      ))}

      <FormSection>
        <SmallActionButton
          type="button"
          onClick={handleAddCase}
          disabled={cases.length >= MAX_PARALLEL_CHILDREN}
          title={
            cases.length >= MAX_PARALLEL_CHILDREN
              ? `A branch can hold at most ${MAX_PARALLEL_CHILDREN} cases`
              : undefined
          }
        >
          + Add case
        </SmallActionButton>
        {cases.length >= MAX_PARALLEL_CHILDREN && (
          <BranchLimitHint>
            Reached the maximum of {MAX_PARALLEL_CHILDREN} cases.
          </BranchLimitHint>
        )}
      </FormSection>

      <BranchOtherwiseCard>
        <strong>Otherwise</strong>
        <span>Fires when no case above matches. Always evaluated last.</span>
      </BranchOtherwiseCard>

      {showProblems && problems.length > 0 && (
        <ValidationProblems role="alert">
          <strong>Fix the following before saving:</strong>
          <ValidationProblemsList>
            {problems.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ValidationProblemsList>
        </ValidationProblems>
      )}

      {!hideSave && <SaveButton type="submit">Update Node</SaveButton>}
    </form>
  )
}

export default BranchNodeForm
